import { useState, useEffect } from 'react';
import { Mail, Phone, X, Send, Loader2 } from 'lucide-react';

interface SendNotificationConfirmDialogProps {
  isOpen: boolean;
  title: string;
  defaultEmail: string;
  defaultPhone: string;
  isLoading?: boolean;
  onConfirm: (email: string, phone: string) => void;
  onCancel: () => void;
}

export function SendNotificationConfirmDialog({
  isOpen,
  title,
  defaultEmail,
  defaultPhone,
  isLoading = false,
  onConfirm,
  onCancel,
}: SendNotificationConfirmDialogProps) {
  const [email, setEmail] = useState(defaultEmail);
  const [phone, setPhone] = useState(defaultPhone);

  useEffect(() => {
    setEmail(defaultEmail);
    setPhone(defaultPhone);
  }, [defaultEmail, defaultPhone]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) onCancel();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLoading, onCancel]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-transition-opacity"
        style={{ zIndex: 50 }}
        onClick={isLoading ? undefined : onCancel}
      />
      <div className="tw-fixed tw-inset-0 tw-overflow-y-auto" style={{ zIndex: 50 }}>
        <div className="tw-flex tw-min-h-full tw-items-center tw-justify-center tw-p-4">
          <div
            className="tw-relative tw-w-full tw-max-w-md tw-bg-white tw-rounded-lg tw-shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="tw-absolute tw-top-4 tw-right-4 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors disabled:tw-opacity-50"
            >
              <X className="tw-w-5 tw-h-5" />
            </button>

            <div className="tw-p-6">
              <div className="tw-flex tw-items-center tw-gap-3 tw-mb-5">
                <div className="tw-flex-shrink-0 tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-bg-affinicar-lighter">
                  <Send className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
                </div>
                <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-m-0">{title}</h3>
              </div>

              <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
                Vérifiez les coordonnées du bénéficiaire avant l'envoi. Un email et un SMS seront envoyés.
              </p>

              <div className="tw-space-y-3">
                <div>
                  <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                    <Mail className="tw-w-3.5 tw-h-3.5" />
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary focus:tw-border-affinicar-primary disabled:tw-opacity-50 disabled:tw-bg-gray-50"
                  />
                </div>
                <div>
                  <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                    <Phone className="tw-w-3.5 tw-h-3.5" />
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={isLoading}
                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary focus:tw-border-affinicar-primary disabled:tw-opacity-50 disabled:tw-bg-gray-50"
                  />
                </div>
              </div>

              <div className="tw-flex tw-justify-end tw-gap-3 tw-mt-6">
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={isLoading}
                  className="tw-px-4 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white hover:tw-bg-gray-50 disabled:tw-opacity-50"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={() => onConfirm(email, phone)}
                  disabled={isLoading || !email.trim()}
                  className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-border tw-border-transparent tw-rounded-md tw-shadow-sm tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary hover:tw-bg-affinicar-secondary disabled:tw-opacity-50"
                >
                  {isLoading ? (
                    <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                  ) : (
                    <Send className="tw-w-4 tw-h-4" />
                  )}
                  Envoyer
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
